import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2, X, Plus, Trash2, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type {
  CreateModuleConfigRequest,
  MigrationProperty,
  MigrationPropertyType,
} from '@/types';
import { ModuleExecutionMode } from '@/types';
import { cn } from '@/lib/utils';

const BRIDGE_PRODUCT_TYPES: Array<{ value: number; label: string }> = [
  { value: -1, label: 'Admin' },
  { value: 1, label: 'SimpleChurchCRM' },
  { value: 2, label: 'Arena' },
  { value: 3, label: 'Financials' },
  { value: 4, label: 'Ezekiel' },
  { value: 5, label: 'Formbuilder' },
  { value: 6, label: 'Giving' },
  { value: 7, label: 'Innovate' },
  { value: 8, label: 'Elexio' },
  { value: 9, label: 'FellowshipOne' },
  { value: 10, label: 'ParishSoft' },
  { value: 11, label: 'ProtectMyMinistry' },
  { value: 12, label: 'CloverSites' },
  { value: 13, label: 'SiteOrganic' },
  { value: 14, label: 'MinistryOne' },
  { value: 15, label: 'ShareFaithWeb' },
  { value: 16, label: 'ChurchStreamingTV' },
  { value: 17, label: 'ShareFaithKids' },
  { value: 18, label: 'E360' },
  { value: 19, label: 'ShelbyFinancials' },
  { value: 20, label: 'WorshipPlanning' },
  { value: 21, label: 'Educate' },
  { value: 22, label: 'TADS' },
  { value: 23, label: 'FaithDirect' },
  { value: 24, label: 'Ravenna' },
  { value: 25, label: 'ShareFaithMedia' },
  { value: 26, label: 'LandingPage' },
  { value: 27, label: 'ProjectUnity' },
  { value: 28, label: 'HGSCommunication' },
  { value: 29, label: 'StudioAI' },
  { value: 30, label: 'CMG' },
  { value: 99, label: 'Formbuilder_Client' },
];

const AMPLIFY_MODULES: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Giving Admin' },
  { value: 2, label: 'People' },
  { value: 3, label: 'Mobile App' },
  { value: 5, label: 'Accounting' },
  { value: 6, label: 'Websites' },
  { value: 7, label: 'Media' },
  { value: 8, label: 'Kids' },
  { value: 9, label: 'Streaming' },
  { value: 10, label: 'Service Planning' },
  { value: 12, label: 'Bible & Sermons' },
  { value: 14, label: 'My Parish' },
  { value: 15, label: 'Families' },
  { value: 16, label: 'Tuition' },
  { value: 17, label: 'Ministry Scheduler' },
  { value: 18, label: 'Faith Formation' },
  { value: 19, label: 'Development' },
  { value: 20, label: 'Offering' },
  { value: 21, label: 'Safety' },
  { value: 22, label: 'Intelligent Query' },
  { value: 23, label: 'Communications' },
  { value: 24, label: 'Equip' },
  { value: 25, label: 'CloverSites' },
  { value: 26, label: 'Amplify Intelligence' },
];

const createModuleSchema = z
  .object({
    moduleKey: z
      .string()
      .min(1, 'Module key is required.')
      .max(100)
      .regex(/^[a-zA-Z0-9-]+$/, 'Only letters, numbers, and hyphens allowed.'),
    displayName: z.string().min(1, 'Display name is required.').max(200),
    migrateEndpoint: z.string().max(500).optional().or(z.literal('')),
    validateEndpoint: z.string().max(500).optional().or(z.literal('')),
    sourceIdValidationRegex: z.string().max(500).optional().or(z.literal('')),
    sourceIdPlaceholder: z.string().max(100).optional().or(z.literal('')),
    timeoutSeconds: z.number().min(30).max(3600),
    maxRetries: z.number().min(0).max(10),
    retryDelaySeconds: z.number().min(0).max(300),
    baseUrl: z.string().url('Must be a valid URL').max(500).optional().or(z.literal('')),
    apiKeySecretName: z.string().max(100).optional().or(z.literal('')),
    appId: z.string().max(100).optional().or(z.literal('')),
    userMigrationGetEndpoint: z.string().max(500).optional().or(z.literal('')),
    userMigrationPutEndpoint: z.string().max(500).optional().or(z.literal('')),
    executionMode: z.enum([ModuleExecutionMode.Synchronous, ModuleExecutionMode.AsyncPolled]),
    jobStatusEndpoint: z.string().max(500).optional().or(z.literal('')),
    pollIntervalSeconds: z.number().min(5).max(3600),
    pollMaxAttempts: z.number().min(1).max(1000),
    metadataUpdatesJson: z.string().optional().or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    // Metadata bindings are independent of execution mode — validate whenever present.
    if (data.metadataUpdatesJson && data.metadataUpdatesJson.trim() !== '') {
      try {
        const parsed = JSON.parse(data.metadataUpdatesJson);
        if (!Array.isArray(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['metadataUpdatesJson'],
            message: 'Must be a JSON array of { metaKey, valueFrom } bindings.',
          });
        }
      } catch {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['metadataUpdatesJson'],
          message: 'Not valid JSON.',
        });
      }
    }

    if (data.executionMode !== ModuleExecutionMode.AsyncPolled) return;

    if (!data.jobStatusEndpoint || data.jobStatusEndpoint.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['jobStatusEndpoint'],
        message: 'Required when execution mode is Async Polled.',
      });
    }
  });

type FormValues = z.infer<typeof createModuleSchema>;

function getDefaultValue(type: MigrationPropertyType): string | number | boolean {
  switch (type) {
    case 'string':
      return '';
    case 'integer':
      return 0;
    case 'boolean':
      return false;
  }
}

interface AddModuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateModuleConfigRequest) => void;
  isSubmitting?: boolean | undefined;
}

export function AddModuleDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: AddModuleDialogProps) {
  const [migrationProperties, setMigrationProperties] = useState<MigrationProperty[]>([]);
  const [bridgeProductTypeId, setBridgeProductTypeId] = useState<number | null>(null);
  const [amplifyModuleId, setAmplifyModuleId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger,
  } = useForm<FormValues>({
    resolver: zodResolver(createModuleSchema),
    defaultValues: {
      moduleKey: '',
      displayName: '',
      migrateEndpoint: '',
      validateEndpoint: '',
      sourceIdValidationRegex: '',
      sourceIdPlaceholder: '',
      timeoutSeconds: 300,
      maxRetries: 3,
      retryDelaySeconds: 30,
      baseUrl: '',
      apiKeySecretName: '',
      appId: '',
      userMigrationGetEndpoint: '',
      userMigrationPutEndpoint: '',
      executionMode: ModuleExecutionMode.Synchronous,
      jobStatusEndpoint: '',
      pollIntervalSeconds: 30,
      pollMaxAttempts: 60,
      metadataUpdatesJson: '',
    },
  });

  const executionMode = watch('executionMode');
  const isAsync = executionMode === ModuleExecutionMode.AsyncPolled;

  // Create is a guided wizard (Edit uses the flat tabbed editor). Each step gates Next on
  // the fields it owns; only Basics has required fields, but later steps still validate
  // format (e.g. base URL) before advancing.
  const WIZARD_STEPS = ['Basics', 'Endpoints', 'Connection', 'Advanced'] as const;
  const STEP_FIELDS: Array<Array<keyof FormValues>> = [
    ['moduleKey', 'displayName', 'timeoutSeconds', 'maxRetries', 'retryDelaySeconds'],
    ['validateEndpoint', 'migrateEndpoint'],
    ['baseUrl', 'apiKeySecretName', 'appId'],
    [
      'sourceIdPlaceholder', 'sourceIdValidationRegex',
      'userMigrationGetEndpoint', 'userMigrationPutEndpoint',
      'jobStatusEndpoint', 'pollIntervalSeconds', 'pollMaxAttempts', 'metadataUpdatesJson',
    ],
  ];
  const lastStep = WIZARD_STEPS.length - 1;
  const [step, setStep] = useState(0);

  // Reset on close via the `open` prop, not onOpenChange: after a successful
  // create the parent closes the dialog programmatically, which bypasses
  // onOpenChange and would leave the previous module's values and step behind.
  useEffect(() => {
    if (!open) {
      reset();
      setMigrationProperties([]);
      setBridgeProductTypeId(null);
      setAmplifyModuleId(null);
      setStep(0);
    }
  }, [open, reset]);

  const goNext = async () => {
    const valid = await trigger(STEP_FIELDS[step]);
    if (valid) setStep((s) => Math.min(s + 1, lastStep));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleFormSubmit = (data: FormValues) => {
    // Only create on the final step — guards against Enter submitting an earlier step.
    if (step !== lastStep) {
      void goNext();
      return;
    }
    const metaFields =
      migrationProperties.length > 0 ? JSON.stringify(migrationProperties) : undefined;

    onSubmit({
      moduleKey: data.moduleKey,
      displayName: data.displayName,
      migrateEndpoint: data.migrateEndpoint || undefined,
      validateEndpoint: data.validateEndpoint || undefined,
      sourceIdValidationRegex: data.sourceIdValidationRegex || undefined,
      sourceIdPlaceholder: data.sourceIdPlaceholder || undefined,
      timeoutSeconds: data.timeoutSeconds,
      maxRetries: data.maxRetries,
      retryDelaySeconds: data.retryDelaySeconds,
      metaFields,
      baseUrl: data.baseUrl || undefined,
      apiKeySecretName: data.apiKeySecretName || undefined,
      appId: data.appId || undefined,
      userMigrationGetEndpoint: data.userMigrationGetEndpoint || undefined,
      userMigrationPutEndpoint: data.userMigrationPutEndpoint || undefined,
      bridgeProductTypeId: bridgeProductTypeId ?? undefined,
      amplifyModuleId: amplifyModuleId ?? undefined,
      executionMode: data.executionMode,
      // Metadata bindings are independent of execution mode — always included.
      metadataUpdatesJson: data.metadataUpdatesJson || undefined,
      ...(data.executionMode === ModuleExecutionMode.AsyncPolled && {
        jobStatusEndpoint: data.jobStatusEndpoint || undefined,
        pollIntervalSeconds: data.pollIntervalSeconds,
        pollMaxAttempts: data.pollMaxAttempts,
      }),
    });
  };

  const handleClose = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const addProperty = () => {
    setMigrationProperties((prev) => [
      ...prev,
      { key: '', label: '', type: 'string', value: '' },
    ]);
  };

  const removeProperty = (index: number) => {
    setMigrationProperties((prev) => prev.filter((_, i) => i !== index));
  };

  const updateProperty = (
    index: number,
    field: keyof MigrationProperty,
    value: string | number | boolean,
  ) => {
    setMigrationProperties((prev) =>
      prev.map((prop, i) => {
        if (i !== index) return prop;

        if (field === 'type') {
          const newType = value as MigrationPropertyType;
          return { ...prop, type: newType, value: getDefaultValue(newType) };
        }

        return { ...prop, [field]: value };
      }),
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] w-[95vw] overflow-y-auto sm:max-w-[920px]">
        <DialogHeader>
          <DialogTitle>Add Module Configuration</DialogTitle>
          <DialogDescription>
            Create a new migration module configuration.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Create is a 4-step guided wizard (Basics → Endpoints → Connection → Advanced).
              The Edit modal keeps the flat tabbed layout. */}
          <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-2">
            {WIZARD_STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full border text-xs font-medium',
                    i < step
                      ? 'border-primary bg-primary text-primary-foreground'
                      : i === step
                        ? 'border-primary text-primary'
                        : 'border-border text-muted-foreground',
                  )}
                >
                  {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span
                  className={cn(
                    'text-sm',
                    i === step ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {label}
                </span>
                {i < WIZARD_STEPS.length - 1 && (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 min-h-[340px]">
            {step === 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="moduleKey">Module Key *</Label>
                  <Input
                    id="moduleKey"
                    placeholder="my-module"
                    className="font-mono text-sm"
                    {...register('moduleKey')}
                  />
                  {errors.moduleKey && (
                    <p className="text-sm text-destructive">{errors.moduleKey.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Unique identifier (letters, numbers, hyphens)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name *</Label>
                  <Input
                    id="displayName"
                    placeholder="My Module"
                    {...register('displayName')}
                  />
                  {errors.displayName && (
                    <p className="text-sm text-destructive">{errors.displayName.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Execution Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setValue('executionMode', ModuleExecutionMode.Synchronous, { shouldDirty: true })}
                    className={cn(
                      'rounded-md border p-3 text-left text-sm transition-colors',
                      executionMode === ModuleExecutionMode.Synchronous
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <div className="font-medium">Synchronous</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Single POST returns success/failure inline (default).
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue('executionMode', ModuleExecutionMode.AsyncPolled, { shouldDirty: true })}
                    className={cn(
                      'rounded-md border p-3 text-left text-sm transition-colors',
                      executionMode === ModuleExecutionMode.AsyncPolled
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <div className="font-medium">Async Polled</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Module returns jobId; orchestrator polls until complete.
                    </div>
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Async-polled modules expose additional configuration in the Advanced step.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-timeoutSeconds">Timeout (seconds)</Label>
                  <Input
                    id="create-timeoutSeconds"
                    type="number"
                    {...register('timeoutSeconds', { valueAsNumber: true })}
                  />
                  {errors.timeoutSeconds && (
                    <p className="text-sm text-destructive">{errors.timeoutSeconds.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">30 - 3600 seconds</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-maxRetries">Max Retries</Label>
                  <Input
                    id="create-maxRetries"
                    type="number"
                    {...register('maxRetries', { valueAsNumber: true })}
                  />
                  {errors.maxRetries && (
                    <p className="text-sm text-destructive">{errors.maxRetries.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-retryDelaySeconds">Retry Delay (seconds)</Label>
                  <Input
                    id="create-retryDelaySeconds"
                    type="number"
                    {...register('retryDelaySeconds', { valueAsNumber: true })}
                  />
                  {errors.retryDelaySeconds && (
                    <p className="text-sm text-destructive">
                      {errors.retryDelaySeconds.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            )}

            {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="create-baseUrl">Base URL</Label>
                <Input
                  id="create-baseUrl"
                  placeholder="https://api.example.com/"
                  className="font-mono text-sm"
                  {...register('baseUrl')}
                />
                {errors.baseUrl && (
                  <p className="text-sm text-destructive">{errors.baseUrl.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Base URL for this module's API calls. Leave empty for system default.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-apiKeySecretName">API Key Secret Name</Label>
                <Input
                  id="create-apiKeySecretName"
                  placeholder="MyModuleApiKey"
                  className="font-mono text-sm"
                  {...register('apiKeySecretName')}
                />
                {errors.apiKeySecretName && (
                  <p className="text-sm text-destructive">{errors.apiKeySecretName.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Name of the configuration key or Key Vault secret holding the API key.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-appId">App ID</Label>
                <Input
                  id="create-appId"
                  placeholder="my-app-id"
                  className="font-mono text-sm"
                  {...register('appId')}
                />
                {errors.appId && (
                  <p className="text-sm text-destructive">{errors.appId.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Optional application identifier sent as X-App-Id header. Required by some platforms (e.g., Giving).
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-bridgeProductTypeId">Bridge Product Type</Label>
                <Select
                  value={bridgeProductTypeId !== null ? String(bridgeProductTypeId) : 'none'}
                  onValueChange={(value) =>
                    setBridgeProductTypeId(value === 'none' ? null : parseInt(value, 10))
                  }
                >
                  <SelectTrigger id="create-bridgeProductTypeId">
                    <SelectValue placeholder="Select product type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {BRIDGE_PRODUCT_TYPES.map((pt) => (
                      <SelectItem key={pt.value} value={String(pt.value)}>
                        {pt.label} ({pt.value})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Maps this module to a Bridge product type for automatic detection during org lookup.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-amplifyModuleId">Amplify Module</Label>
                <Select
                  value={amplifyModuleId !== null ? String(amplifyModuleId) : 'none'}
                  onValueChange={(value) =>
                    setAmplifyModuleId(value === 'none' ? null : parseInt(value, 10))
                  }
                >
                  <SelectTrigger id="create-amplifyModuleId">
                    <SelectValue placeholder="Select Amplify module..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {AMPLIFY_MODULES.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>
                        {m.label} ({m.value})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Amplify module to provision after migration. Must match the Amplify platform's module ID.
                </p>
              </div>
            </div>
            )}

            {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="create-validateEndpoint">Validate Endpoint</Label>
                <Input
                  id="create-validateEndpoint"
                  placeholder="/api/validate"
                  {...register('validateEndpoint')}
                />
                {errors.validateEndpoint && (
                  <p className="text-sm text-destructive">
                    {errors.validateEndpoint.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-migrateEndpoint">Migration Endpoint</Label>
                <Input
                  id="create-migrateEndpoint"
                  placeholder="/api/migrate"
                  {...register('migrateEndpoint')}
                />
                {errors.migrateEndpoint && (
                  <p className="text-sm text-destructive">
                    {errors.migrateEndpoint.message}
                  </p>
                )}
              </div>

              <div className="border-t pt-4 space-y-4">
                <div>
                  <h4 className="text-sm font-medium">User Migration (Optional)</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Set the GET endpoint to enable Phase 2 user migration for this module. The
                    PUT (UUID write-back) endpoint is optional — leave it blank for modules that
                    already record the Amplify UUID during provisioning, and the write-back step
                    is skipped. Use <code className="font-mono">{'{sourceId}'}</code> as a placeholder.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-userMigrationGetEndpoint">GET Users Endpoint</Label>
                  <Input
                    id="create-userMigrationGetEndpoint"
                    placeholder="/api/migration/users/{sourceId}"
                    className="font-mono text-sm"
                    {...register('userMigrationGetEndpoint')}
                  />
                  {errors.userMigrationGetEndpoint && (
                    <p className="text-sm text-destructive">
                      {errors.userMigrationGetEndpoint.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="create-userMigrationPutEndpoint">
                    PUT UUIDs Endpoint <span className="text-muted-foreground font-normal">(optional — blank skips write-back)</span>
                  </Label>
                  <Input
                    id="create-userMigrationPutEndpoint"
                    placeholder="/api/migration/users/{sourceId}"
                    className="font-mono text-sm"
                    {...register('userMigrationPutEndpoint')}
                  />
                  {errors.userMigrationPutEndpoint && (
                    <p className="text-sm text-destructive">
                      {errors.userMigrationPutEndpoint.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            )}

            {step === 3 && (
            <div className="space-y-6">
            <div className="space-y-4">
              {!isAsync ? (
                <div className="rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">
                  Module is in <span className="font-medium">Synchronous</span> mode. Switch to{' '}
                  <span className="font-medium">Async Polled</span> in the Basics step to configure
                  job polling.
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="create-jobStatusEndpoint">Job Status Endpoint</Label>
                    <Input
                      id="create-jobStatusEndpoint"
                      placeholder="api/jobs/{jobId}"
                      className="font-mono text-sm"
                      {...register('jobStatusEndpoint')}
                    />
                    {errors.jobStatusEndpoint && (
                      <p className="text-sm text-destructive">{errors.jobStatusEndpoint.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      GET endpoint the orchestrator polls until the module job completes. Use{' '}
                      <code className="font-mono">{'{jobId}'}</code> as a placeholder.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="create-pollIntervalSeconds">Poll Interval (seconds)</Label>
                      <Input
                        id="create-pollIntervalSeconds"
                        type="number"
                        {...register('pollIntervalSeconds', { valueAsNumber: true })}
                      />
                      {errors.pollIntervalSeconds && (
                        <p className="text-sm text-destructive">
                          {errors.pollIntervalSeconds.message}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">Minimum 5 seconds.</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="create-pollMaxAttempts">Max Poll Attempts</Label>
                      <Input
                        id="create-pollMaxAttempts"
                        type="number"
                        {...register('pollMaxAttempts', { valueAsNumber: true })}
                      />
                      {errors.pollMaxAttempts && (
                        <p className="text-sm text-destructive">{errors.pollMaxAttempts.message}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Total wait ≈ interval × attempts.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="create-metadataUpdatesJson">
                  Metadata Update Bindings (optional)
                </Label>
                <textarea
                  id="create-metadataUpdatesJson"
                  rows={6}
                  className="font-mono text-xs w-full rounded-md border border-input bg-background px-3 py-2"
                  placeholder='[{"metaKey":"plan_type","valueFrom":"$.meta.planType"}]'
                  {...register('metadataUpdatesJson')}
                />
                {errors.metadataUpdatesJson && (
                  <p className="text-sm text-destructive">
                    {errors.metadataUpdatesJson.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  JSON array of{' '}
                  <code className="font-mono">{'{ metaKey, valueFrom }'}</code> bindings, applied
                  for <span className="font-medium">both execution modes</span> whenever bindings
                  are present. Each <code className="font-mono">valueFrom</code> is a JSONPath
                  expression (e.g. <code className="font-mono">$.meta.planType</code>) evaluated
                  against the migrate endpoint's response body (Synchronous) or the job status
                  result (Async Polled); the resolved value may be a string, number, or boolean.
                  After the module is confirmed in Amplify, the orchestrator PATCHes
                  <code className="font-mono"> /modules/{'{moduleId}'}/meta</code> once per
                  binding.
                </p>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="create-sourceIdPlaceholder">ID Format (Display)</Label>
                <Input
                  id="create-sourceIdPlaceholder"
                  placeholder="ORG-XXXXX"
                  {...register('sourceIdPlaceholder')}
                />
                <p className="text-xs text-muted-foreground">
                  Human-readable format shown to users
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-sourceIdValidationRegex">ID Validation Regex</Label>
                <Input
                  id="create-sourceIdValidationRegex"
                  placeholder="^ORG-[A-Z0-9]{5}$"
                  className="font-mono text-sm"
                  {...register('sourceIdValidationRegex')}
                />
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium">Custom Properties</h4>
                  <p className="text-xs text-muted-foreground">
                    Static configuration values sent to the platform API during migration.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addProperty}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add Property
                </Button>
              </div>

              {migrationProperties.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border rounded-lg bg-muted/30">
                  <p className="text-sm">No migration properties configured.</p>
                  <p className="text-xs mt-1">
                    Add properties that will be included in every migration request for this
                    module.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {migrationProperties.map((prop, index) => (
                    <div key={index} className="border rounded-lg p-3 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="grid grid-cols-2 gap-3 flex-1">
                          <div className="space-y-1">
                            <Label className="text-xs">Key</Label>
                            <Input
                              value={prop.key}
                              onChange={(e) => updateProperty(index, 'key', e.target.value)}
                              placeholder="pricingPlan"
                              className="font-mono text-sm h-8"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Label</Label>
                            <Input
                              value={prop.label}
                              onChange={(e) =>
                                updateProperty(index, 'label', e.target.value)
                              }
                              placeholder="Pricing Plan"
                              className="text-sm h-8"
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 ml-2 text-muted-foreground hover:text-destructive"
                          onClick={() => removeProperty(index)}
                          aria-label={`Remove property ${prop.key || index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Type</Label>
                          <Select
                            value={prop.type}
                            onValueChange={(value: MigrationPropertyType) =>
                              updateProperty(index, 'type', value)
                            }
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="string">String</SelectItem>
                              <SelectItem value="integer">Integer</SelectItem>
                              <SelectItem value="boolean">Boolean</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Value</Label>
                          {prop.type === 'boolean' ? (
                            <div className="flex items-center h-8 gap-2">
                              <Switch
                                checked={prop.value === true}
                                onCheckedChange={(checked) =>
                                  updateProperty(index, 'value', checked)
                                }
                              />
                              <span className="text-sm text-muted-foreground">
                                {prop.value === true ? 'true' : 'false'}
                              </span>
                            </div>
                          ) : prop.type === 'integer' ? (
                            <Input
                              type="number"
                              value={typeof prop.value === 'number' ? prop.value : ''}
                              onChange={(e) =>
                                updateProperty(
                                  index,
                                  'value',
                                  e.target.value === '' ? 0 : parseInt(e.target.value, 10),
                                )
                              }
                              className="text-sm h-8"
                            />
                          ) : (
                            <Input
                              value={
                                typeof prop.value === 'string'
                                  ? prop.value
                                  : String(prop.value)
                              }
                              onChange={(e) =>
                                updateProperty(index, 'value', e.target.value)
                              }
                              placeholder="value"
                              className="text-sm h-8"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
            )}
          </div>

          <DialogFooter className="mt-6 flex items-center sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={step === 0 ? () => handleClose(false) : goBack}
              disabled={isSubmitting}
            >
              {step === 0 ? (
                <>
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </>
              ) : (
                <>
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Back
                </>
              )}
            </Button>

            {/* Distinct keys + type="button" on Create are load-bearing: without them React
                reuses the same DOM node for Next/Create, and the click that advances to the
                last step re-targets it as a submit button during the same event's default
                action — instantly creating the module and skipping the Advanced step. Enter
                still submits via the form's onSubmit, which routes through the step guard. */}
            {step < lastStep ? (
              <Button key="wizard-next" type="button" onClick={goNext} disabled={isSubmitting}>
                Next
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                key="wizard-create"
                type="button"
                onClick={handleSubmit(handleFormSubmit)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Create Module
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
